import React, { useState } from 'react'
import { Button, Input, Option, Select, Spinner, Textarea, Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import { useFormData } from '../hooks/form'
import { useGeneralToast } from '../hooks/toast'
import { createOrEditPattern } from '../utils/helpers/supabase.helpers'
import { AGAMI_GLOBAL_EXPERIENCE_ID } from '../utils/constants/db.constants'
import { useSessionContext } from '../contexts/SessionContext'
import { useDarkMode } from '../contexts/DarkModeContext'
import { AgamiPattern } from '../types/supabase'

const GLOBAL_EXP = {
  id: AGAMI_GLOBAL_EXPERIENCE_ID,
  name: 'Agami Global',
}

interface ExperienceOption {
  id: string
  name: string
}

interface EditPatternFormProps {
  pattern?: AgamiPattern
  experienceList: ExperienceOption[]
  refetch: () => void
  closeDialog?: () => void
}

const EditPatternForm: React.FC<EditPatternFormProps> = ({
  pattern,
  experienceList,
  refetch,
  closeDialog,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const toast = useGeneralToast()
  const [loading, setLoading] = useState(false)

  const { user } = useSessionContext()

  const { formData, handleDataChange } = useFormData({
    pattern_id: pattern?.pattern_id,
    experience_id: pattern?.experience_id ?? AGAMI_GLOBAL_EXPERIENCE_ID,
    creator_id: user?.id,
    name: pattern?.name,
    description: pattern?.description,
    color: pattern?.color,
  })

  const experiencesOptions = [GLOBAL_EXP].concat(experienceList)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()

    setLoading(true)

    if (!formData.experience_id) {
      toast.error(t('messages.choosePatternExperienceError'))
    }

    if (!formData.name) {
      toast.error(t('messages.choosePatternNameError'))
    }

    const { error } = await createOrEditPattern({
      upsertData: formData,
    })

    if (error) {
      toast.error(typeof error === 'string' ? error : ((error as Error)?.message ?? JSON.stringify(error)))
      setLoading(false)
      return
    }

    refetch()
    setLoading(false)
    if (closeDialog) closeDialog()
    toast.success(pattern ? t('messages.patternUpdatedSuccess') : t('messages.patternCreatedSuccess'))
  }

  return (
    <form className="flex flex-col gap-4 px-2" onSubmit={onSubmit} data-testid="pattern-form">
      <div className="flex flex-col">
        <Select
          label={t('forms.chooseExperience')}
          value={formData.experience_id ?? ''}
          onChange={(val) => handleDataChange('experience_id', val || '')}
          menuProps={{ className: 'dark:bg-primary' }}
          data-testid="pattern-experience-select"
          {...(darkMode && { color: 'blue-gray' })}
          labelProps={{
            className:
              'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
          }}
        >
          {experiencesOptions.map((exp) => {
            const isSelected = exp.id === formData.experience_id
            return (
              <Option
                key={exp.id}
                value={exp.id}
                data-testid={
                  exp.id === AGAMI_GLOBAL_EXPERIENCE_ID
                    ? 'pattern-experience-option-global'
                    : `pattern-experience-option-${exp.id}`
                }
                {...(isSelected && { className: 'bg-gray-200 dark:bg-primary-300' })}
              >
                <span className="text-primary dark:text-white">{exp.name}</span>
              </Option>
            )
          })}
        </Select>
        <Typography variant="small">{t('forms.targetExperience')}</Typography>
      </div>

      <div className="flex flex-col">
        <Input
          value={formData.name ?? ''}
          onChange={(e) => handleDataChange('name', e.currentTarget.value)}
          type="text"
          label={t('forms.patternName')}
          required
          data-testid="pattern-name-input"
        />
        <Typography variant="small">{t('forms.patternNameHelp')}</Typography>
      </div>

      <div className="flex flex-col">
        <Textarea
          className="w-full -mb-2 text-primary-600 focus:text-primary-700 dark:!text-base-gray-500 dark:focus:text-base-gray-400"
          rows={1}
          value={formData.description}
          onChange={(e) => handleDataChange('description', e.currentTarget.value)}
          label={t('forms.description')}
          {...(darkMode && { color: 'blue-gray' })}
          labelProps={{
            className:
              'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
          }}
        />
        <Typography variant="small">{t('forms.descriptionHelp')}</Typography>
      </div>

      <div className="flex flex-col">
        <Input
          value={formData.color ?? ''}
          onChange={(e) => handleDataChange('color', e.currentTarget.value)}
          type="color"
          label={t('forms.color')}
          required
        />
        <Typography variant="small">{t('forms.colorHelp')}</Typography>
      </div>
      <Button
        type="submit"
        disabled={loading || !formData.experience_id || !formData.name}
        data-testid="pattern-submit-btn"
      >
        {loading && <Spinner className="mx-auto" />}
        {!loading && pattern && t('forms.updatePattern')}
        {!loading && !pattern && t('forms.createPattern')}
      </Button>
    </form>
  )
}

export default EditPatternForm
